import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type {
  OperatingAirline,
  OperatingAirlineCreateRequest,
  OperatingAirlineUpdateRequest,
} from '@/api/client/models/index.ts';
import { useApiClient } from '@/hooks/api-hook.ts';
import { getDropdownOptions } from '@/modules/dataParser.js';
import { buildAbstractMap } from './common.ts';

type LegacyOperatingAirline = OperatingAirline & {
  isActive?: boolean | null;
};

function normalizeOperatingAirline(
  entry: OperatingAirline | null | undefined,
  showOnlyActive?: boolean
): LegacyOperatingAirline | undefined {
  if (!entry) {
    return undefined;
  }

  return {
    ...entry,
    isActive: showOnlyActive ?? entry.isActive ?? null,
  };
}

export const useOperatingAirlinesStore = defineStore('operatingAirlines', () => {
  const apiClient = useApiClient();

  const list = ref<LegacyOperatingAirline[]>([]);
  const abstract = ref<Record<string, string> | null>(null);
  const added = ref<LegacyOperatingAirline | null>(null);
  const loading = ref<boolean>(true);

  const options = computed(() => getDropdownOptions(abstract.value));

  async function add(data: Partial<OperatingAirlineCreateRequest>): Promise<LegacyOperatingAirline | undefined> {
    const response = await apiClient.operatingAirline.post(data as OperatingAirlineCreateRequest);
    added.value = normalizeOperatingAirline(response) ?? null;
    return added.value ?? undefined;
  }

  async function toggle({ id, active }: { id: number; active: boolean }): Promise<ArrayBuffer | undefined> {
    return await apiClient.operatingAirline.byId(id).byIsActive(active).post();
  }

  async function update(data: Partial<OperatingAirlineUpdateRequest>): Promise<ArrayBuffer | undefined> {
    return await apiClient.operatingAirline.put(data as OperatingAirlineUpdateRequest);
  }

  async function get({ showOnlyActive }: { showOnlyActive?: boolean } = {}): Promise<LegacyOperatingAirline[]> {
    loading.value = true;

    try {
      const response = await apiClient.operatingAirline.get({
        queryParameters: {
          isActive: showOnlyActive,
        },
      });

      list.value = (response ?? [])
        .map(entry => normalizeOperatingAirline(entry, showOnlyActive))
        .filter((entry): entry is LegacyOperatingAirline => Boolean(entry));
      abstract.value = buildAbstractMap(list.value, entry => entry.code);
      return list.value;
    } finally {
      loading.value = false;
    }
  }

  return {
    list,
    abstract,
    added,
    loading,
    options,
    add,
    toggle,
    update,
    get,
  };
});
